import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  ArrowsLeftRight,
  Plus,
  Warehouse,
  Package,
  CookingPot,
  Check,
  X,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight,
  MagnifyingGlass,
  Trash,
  Pencil,
  CalendarBlank,
  ClockCounterClockwise
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

// Parse unit string to extract conversion info
const parseUnitString = (unitStr) => {
  if (!unitStr) return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: 1 };
  
  const str = unitStr.toUpperCase();
  let match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(LB|LBS)/i);
  if (match) return { primaryUnit: 'CS', secondaryUnit: 'LB', conversion: parseFloat(match[2]) };
  
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(EA|BAG|BAGS|PC|PCS)/i);
  if (match) return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: parseFloat(match[2]) };
  
  match = str.match(/1\s*(CS|CASE)\s*=\s*([\d.]+)\s*(OZ)/i);
  if (match) return { primaryUnit: 'CS', secondaryUnit: 'OZ', conversion: parseFloat(match[2]) };
  
  if (str.includes('EA') || str.includes('EACH')) return { primaryUnit: 'EA', secondaryUnit: null, conversion: 1 };
  
  return { primaryUnit: 'CS', secondaryUnit: 'EA', conversion: 1 };
};

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  rejected: "bg-red-100 text-red-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-800"
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  completed: Check,
  cancelled: X
};

export default function Transfers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [prepItems, setPrepItems] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Form state
  const [fromLocation, setFromLocation] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [transferItems, setTransferItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [itemType, setItemType] = useState("product");  // "product" or "prep"

  // Admin edit/backdate state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [backdateMode, setBackdateMode] = useState(false);
  const [transferDate, setTransferDate] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const [locRes, prodRes, prepRes] = await Promise.all([
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/prep-items`, { withCredentials: true })
      ]);
      setLocations(locRes.data);
      setProducts(prodRes.data);
      setPrepItems(prepRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransfers = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/transfers${params}`, { withCredentials: true });
      setTransfers(response.data);
    } catch (error) {
      console.error("Failed to load transfers");
    }
  };

  const handleAddItem = (itemId, type = "product") => {
    // Check if item already added
    if (transferItems.find(i => i.item_id === itemId && i.item_type === type)) {
      toast.error("Item already added");
      return;
    }
    
    if (type === "prep") {
      const prep = prepItems.find(p => p.prep_id === itemId);
      if (prep) {
        setTransferItems(prev => [...prev, {
          item_type: "prep",
          item_id: itemId,
          item_name: prep.name,
          item_unit: `${prep.yield_unit || 'EA'} (pan)`,
          pans_to_oz: prep.pans_to_oz || 0,
          quantity_pans: ""
        }]);
      }
    } else {
      const product = products.find(p => p.product_id === itemId);
      if (product) {
        setTransferItems(prev => [...prev, {
          item_type: "product",
          item_id: itemId,
          item_name: product.name,
          item_unit: product.unit,
          price: product.price || 0,
          quantity_cs: "",
          quantity_ea: "",
          quantity_lb: "",
          quantity_oz: ""
        }]);
      }
    }
    setItemSearch("");
  };

  const handleRemoveItem = (itemId, itemType) => {
    setTransferItems(prev => prev.filter(i => !(i.item_id === itemId && i.item_type === itemType)));
  };

  const handleItemChange = (itemId, itemType, field, value) => {
    setTransferItems(prev => prev.map(item => 
      (item.item_id === itemId && item.item_type === itemType) ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = async () => {
    if (!fromLocation || !toLocation) {
      toast.error("Please select source and destination locations");
      return;
    }
    if (fromLocation === toLocation) {
      toast.error("Source and destination must be different");
      return;
    }
    if (transferItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    const hasQuantity = transferItems.some(item => {
      if (item.item_type === "prep") {
        return parseFloat(item.quantity_pans) > 0;
      }
      return parseFloat(item.quantity_cs) > 0 || 
             parseFloat(item.quantity_ea) > 0 || 
             parseFloat(item.quantity_lb) > 0 ||
             parseFloat(item.quantity_oz) > 0;
    });

    if (!hasQuantity) {
      toast.error("Please enter quantities for at least one item");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/transfers`, {
        from_location_id: fromLocation,
        to_location_id: toLocation,
        items: transferItems.map(item => {
          if (item.item_type === "prep") {
            return {
              item_type: "prep",
              item_id: item.item_id,
              quantity_pans: parseFloat(item.quantity_pans) || 0
            };
          }
          return {
            item_type: "product",
            item_id: item.item_id,
            quantity_cs: parseFloat(item.quantity_cs) || 0,
            quantity_ea: parseFloat(item.quantity_ea) || 0,
            quantity_lb: parseFloat(item.quantity_lb) || 0,
            quantity_oz: parseFloat(item.quantity_oz) || 0
          };
        }),
        notes
      }, { withCredentials: true });
      
      toast.success("Transfer request created");
      setDialogOpen(false);
      resetForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create transfer");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (transferId) => {
    try {
      await axios.put(`${API}/transfers/${transferId}/approve`, {}, { withCredentials: true });
      toast.success("Transfer approved");
      fetchTransfers();
    } catch (error) {
      toast.error("Failed to approve transfer");
    }
  };

  const handleReject = async (transferId) => {
    if (!window.confirm("Reject this transfer?")) return;
    try {
      await axios.put(`${API}/transfers/${transferId}/reject`, {}, { withCredentials: true });
      toast.success("Transfer rejected");
      fetchTransfers();
    } catch (error) {
      toast.error("Failed to reject transfer");
    }
  };

  const handleReceive = async (transferId) => {
    try {
      await axios.put(`${API}/transfers/${transferId}/receive`, {}, { withCredentials: true });
      toast.success("Transfer received and completed");
      fetchTransfers();
    } catch (error) {
      toast.error("Failed to receive transfer");
    }
  };

  const handleCancel = async (transferId) => {
    if (!window.confirm("Cancel this transfer?")) return;
    try {
      await axios.put(`${API}/transfers/${transferId}/cancel`, {}, { withCredentials: true });
      toast.success("Transfer cancelled");
      fetchTransfers();
    } catch (error) {
      toast.error("Failed to cancel transfer");
    }
  };

  const resetForm = () => {
    setFromLocation("");
    setToLocation("");
    setTransferItems([]);
    setNotes("");
    setItemSearch("");
    setItemType("product");
    setBackdateMode(false);
    setTransferDate(new Date());
    setEditingTransfer(null);
  };

  // Admin: Open edit dialog for existing transfer
  const handleEditTransfer = (transfer) => {
    setEditingTransfer(transfer);
    setFromLocation(transfer.from_location_id);
    setToLocation(transfer.to_location_id);
    setNotes(transfer.notes || "");
    setTransferDate(new Date(transfer.created_at));
    
    // Convert items back to editable format
    const items = (transfer.items || []).map(item => {
      if (item.item_type === "prep") {
        const prep = prepItems.find(p => p.prep_id === item.item_id);
        return {
          item_type: "prep",
          item_id: item.item_id,
          item_name: item.item_name || item.item_name_snapshot || prep?.name || "Unknown",
          item_unit: item.unit_snapshot || `${prep?.yield_unit || 'EA'} (pan)`,
          quantity_pans: item.quantity_pans || item.total_quantity || ""
        };
      }
      const product = products.find(p => p.product_id === (item.item_id || item.product_id));
      return {
        item_type: "product",
        item_id: item.item_id || item.product_id,
        item_name: item.item_name || item.product_name || item.item_name_snapshot || product?.name || "Unknown",
        item_unit: item.unit_snapshot || product?.unit || "",
        price: item.price_snapshot || product?.price || 0,
        quantity_cs: item.quantity_cs || "",
        quantity_ea: item.quantity_ea || "",
        quantity_lb: item.quantity_lb || "",
        quantity_oz: item.quantity_oz || ""
      };
    });
    setTransferItems(items);
    setEditDialogOpen(true);
  };

  // Admin: Submit edit
  const handleEditSubmit = async () => {
    if (!editingTransfer) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/transfers/${editingTransfer.transfer_id}`, {
        from_location_id: fromLocation,
        to_location_id: toLocation,
        items: transferItems.map(item => {
          if (item.item_type === "prep") {
            return {
              item_type: "prep",
              item_id: item.item_id,
              quantity_pans: parseFloat(item.quantity_pans) || 0
            };
          }
          return {
            item_type: "product",
            item_id: item.item_id,
            quantity_cs: parseFloat(item.quantity_cs) || 0,
            quantity_ea: parseFloat(item.quantity_ea) || 0,
            quantity_lb: parseFloat(item.quantity_lb) || 0,
            quantity_oz: parseFloat(item.quantity_oz) || 0
          };
        }),
        notes,
        created_at: format(transferDate, 'yyyy-MM-dd')
      }, { withCredentials: true });
      
      toast.success("Transfer updated");
      setEditDialogOpen(false);
      resetForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update transfer");
    } finally {
      setSaving(false);
    }
  };

  // Admin: Open backdated transfer dialog
  const handleOpenBackdateDialog = () => {
    resetForm();
    setBackdateMode(true);
    setDialogOpen(true);
  };

  // Admin: Submit backdated transfer
  const handleBackdatedSubmit = async () => {
    if (!fromLocation || !toLocation) {
      toast.error("Please select source and destination locations");
      return;
    }
    if (fromLocation === toLocation) {
      toast.error("Source and destination must be different");
      return;
    }
    if (transferItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    const hasQuantity = transferItems.some(item => {
      if (item.item_type === "prep") {
        return parseFloat(item.quantity_pans) > 0;
      }
      return parseFloat(item.quantity_cs) > 0 || 
             parseFloat(item.quantity_ea) > 0 || 
             parseFloat(item.quantity_lb) > 0 ||
             parseFloat(item.quantity_oz) > 0;
    });

    if (!hasQuantity) {
      toast.error("Please enter quantities for at least one item");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/transfers/backdated`, {
        from_location_id: fromLocation,
        to_location_id: toLocation,
        items: transferItems.map(item => {
          if (item.item_type === "prep") {
            return {
              item_type: "prep",
              item_id: item.item_id,
              quantity_pans: parseFloat(item.quantity_pans) || 0
            };
          }
          return {
            item_type: "product",
            item_id: item.item_id,
            quantity_cs: parseFloat(item.quantity_cs) || 0,
            quantity_ea: parseFloat(item.quantity_ea) || 0,
            quantity_lb: parseFloat(item.quantity_lb) || 0,
            quantity_oz: parseFloat(item.quantity_oz) || 0
          };
        }),
        notes,
        created_at: format(transferDate, 'yyyy-MM-dd')
      }, { withCredentials: true });
      
      toast.success("Backdated transfer created and completed");
      setDialogOpen(false);
      resetForm();
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create backdated transfer");
    } finally {
      setSaving(false);
    }
  };

  const getFilteredItems = () => {
    if (itemType === "prep") {
      if (!itemSearch) return prepItems.slice(0, 20);
      return prepItems.filter(p => 
        p.name.toLowerCase().includes(itemSearch.toLowerCase())
      ).slice(0, 20);
    }
    if (!itemSearch) return products.slice(0, 20);
    return products.filter(p => 
      p.name.toLowerCase().includes(itemSearch.toLowerCase())
    ).slice(0, 20);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="transfers-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            TRANSFERS
          </h1>
          <p className="text-muted-foreground mt-1">
            Transfer products and prep items between locations
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={handleOpenBackdateDialog}
              data-testid="backdate-transfer-btn"
            >
              <ClockCounterClockwise size={18} className="mr-2" />
              Backdate Transfer
            </Button>
          )}
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-primary" data-testid="create-transfer-btn">
            <Plus size={18} className="mr-2" />
            New Transfer
          </Button>
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {["all", "pending", "approved", "completed", "rejected", "cancelled"].map(status => (
          <Button
            key={status}
            variant={statusFilter === status ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(status)}
            className="capitalize"
          >
            {status}
          </Button>
        ))}
      </div>

      {/* Transfers List */}
      <div className="space-y-4">
        {transfers.length > 0 ? (
          transfers.map(transfer => {
            const StatusIcon = statusIcons[transfer.status] || Clock;
            return (
              <Card key={transfer.transfer_id} className="border rounded-xl" data-testid={`transfer-${transfer.transfer_id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Transfer Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className={statusColors[transfer.status]}>
                          <StatusIcon size={14} className="mr-1" />
                          {transfer.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(transfer.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <Warehouse size={18} className="text-muted-foreground" />
                          <span className="font-medium">{transfer.from_location_name}</span>
                        </div>
                        <ArrowRight size={20} className="text-primary" />
                        <div className="flex items-center gap-2">
                          <Warehouse size={18} className="text-muted-foreground" />
                          <span className="font-medium">{transfer.to_location_name}</span>
                        </div>
                      </div>
                      
                      <div className="text-sm text-muted-foreground mb-2">
                        Created by: {transfer.created_by_name}
                        {transfer.approved_by_name && ` • Approved by: ${transfer.approved_by_name}`}
                        {transfer.received_by_name && ` • Received by: ${transfer.received_by_name}`}
                      </div>
                      
                      {transfer.notes && (
                        <p className="text-sm text-muted-foreground italic">"{transfer.notes}"</p>
                      )}
                      
                      {/* Items */}
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">Items ({transfer.items?.length || 0})</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {transfer.items?.slice(0, 4).map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                              <span className="text-sm truncate">{item.product_name}</span>
                              <span className="text-sm font-medium">{item.total_quantity?.toFixed(2)} {item.unit}</span>
                            </div>
                          ))}
                          {transfer.items?.length > 4 && (
                            <div className="text-sm text-muted-foreground p-2">
                              +{transfer.items.length - 4} more items
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Value & Actions */}
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Value</p>
                        <p className="text-xl font-semibold text-primary">${transfer.total_value?.toFixed(2)}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        {/* Admin edit button */}
                        {isAdmin && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditTransfer(transfer)}
                            data-testid={`edit-transfer-${transfer.transfer_id}`}
                          >
                            <Pencil size={16} className="mr-1" />
                            Edit
                          </Button>
                        )}
                        
                        {/* Admin actions for pending transfers */}
                        {isAdmin && transfer.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => handleApprove(transfer.transfer_id)} className="bg-green-600 hover:bg-green-700">
                              <Check size={16} className="mr-1" />
                              Approve
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleReject(transfer.transfer_id)}>
                              <X size={16} className="mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        
                        {/* Receive button for approved transfers */}
                        {transfer.status === "approved" && (
                          <Button size="sm" onClick={() => handleReceive(transfer.transfer_id)} className="bg-blue-600 hover:bg-blue-700">
                            <CheckCircle size={16} className="mr-1" />
                            Mark Received
                          </Button>
                        )}
                        
                        {/* Cancel button for pending/approved */}
                        {["pending", "approved"].includes(transfer.status) && (
                          <Button size="sm" variant="outline" onClick={() => handleCancel(transfer.transfer_id)}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="border rounded-xl">
            <CardContent className="p-8 text-center text-muted-foreground">
              No transfers found. Create a new transfer to move products between locations.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Transfer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">
              {backdateMode ? "BACKDATE TRANSFER" : "NEW TRANSFER"}
            </DialogTitle>
            {backdateMode && (
              <p className="text-sm text-muted-foreground">
                Create a historical transfer that will be logged as completed immediately.
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Backdate: Date Picker */}
            {backdateMode && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="flex items-center gap-2 text-amber-800 mb-2">
                  <CalendarBlank size={18} />
                  Transfer Date
                </Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start" data-testid="backdate-picker">
                      <CalendarBlank size={18} className="mr-2" />
                      {format(transferDate, 'MMMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={transferDate}
                      onSelect={(date) => {
                        if (date) setTransferDate(date);
                        setDatePickerOpen(false);
                      }}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
            
            {/* Locations */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Location</Label>
                <Select value={fromLocation} onValueChange={setFromLocation}>
                  <SelectTrigger data-testid="from-location-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Location</Label>
                <Select value={toLocation} onValueChange={setToLocation}>
                  <SelectTrigger data-testid="to-location-select">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.filter(l => l.location_id !== fromLocation).map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Items - Products or Prep Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Add Items</Label>
                <div className="flex gap-1">
                  <Button 
                    variant={itemType === "product" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setItemType("product")}
                    className="h-7 text-xs"
                  >
                    <Package size={14} className="mr-1" />
                    Products
                  </Button>
                  <Button 
                    variant={itemType === "prep" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setItemType("prep")}
                    className="h-7 text-xs"
                  >
                    <CookingPot size={14} className="mr-1" />
                    Prep Items
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input 
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder={`Search ${itemType === "prep" ? "prep items" : "products"}...`}
                    className="pl-9"
                    data-testid="item-search-input"
                  />
                </div>
              </div>
              {itemSearch && (
                <div className="mt-2 border rounded-lg max-h-[150px] overflow-y-auto">
                  {getFilteredItems().map(item => (
                    <div 
                      key={itemType === "prep" ? item.prep_id : item.product_id}
                      className="flex items-center justify-between p-2 hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleAddItem(itemType === "prep" ? item.prep_id : item.product_id, itemType)}
                    >
                      <div className="flex items-center gap-2">
                        {itemType === "prep" ? (
                          <CookingPot size={14} className="text-secondary" />
                        ) : (
                          <Package size={14} className="text-primary" />
                        )}
                        <span className="text-sm">{item.name}</span>
                      </div>
                      <Plus size={16} className="text-primary" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Transfer Items */}
            {transferItems.length > 0 && (
              <div>
                <Label>Transfer Items ({transferItems.length})</Label>
                <div className="space-y-3 mt-2">
                  {transferItems.map((item, idx) => {
                    const isPrep = item.item_type === "prep";
                    const unitInfo = isPrep ? null : parseUnitString(item.item_unit);
                    return (
                      <div key={`${item.item_type}-${item.item_id}-${idx}`} className="p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {isPrep ? (
                              <CookingPot size={16} className="text-secondary" />
                            ) : (
                              <Package size={16} className="text-primary" />
                            )}
                            <span className="font-medium">{item.item_name}</span>
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveItem(item.item_id, item.item_type)}>
                            <Trash size={14} className="text-destructive" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{item.item_unit}</p>
                        
                        {isPrep ? (
                          /* Prep items - pans only */
                          <div className="flex gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={item.quantity_pans}
                                onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_pans', e.target.value)}
                                placeholder="0"
                                className="w-20 h-8 text-center text-sm"
                                min="0"
                                step="0.5"
                                data-testid={`pans-input-${item.item_id}`}
                              />
                              <span className="text-xs text-muted-foreground">pans</span>
                            </div>
                            {item.pans_to_oz > 0 && (
                              <span className="text-xs text-muted-foreground self-center">
                                ({item.pans_to_oz} oz/pan)
                              </span>
                            )}
                          </div>
                        ) : (
                          /* Products - CS, EA, LB, OZ */
                          <div className="flex gap-2 flex-wrap">
                            {unitInfo?.primaryUnit === 'CS' && (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={item.quantity_cs}
                                  onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_cs', e.target.value)}
                                  placeholder="0"
                                  className="w-20 h-8 text-center text-sm"
                                  min="0"
                                />
                                <span className="text-xs text-muted-foreground">CS</span>
                              </div>
                            )}
                            {unitInfo?.secondaryUnit === 'EA' && (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={item.quantity_ea}
                                  onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_ea', e.target.value)}
                                  placeholder="0"
                                  className="w-20 h-8 text-center text-sm"
                                  min="0"
                                />
                                <span className="text-xs text-muted-foreground">EA</span>
                              </div>
                            )}
                            {unitInfo?.secondaryUnit === 'LB' && (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={item.quantity_lb}
                                  onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_lb', e.target.value)}
                                  placeholder="0"
                                  className="w-20 h-8 text-center text-sm"
                                  min="0"
                                />
                                <span className="text-xs text-muted-foreground">LB</span>
                              </div>
                            )}
                            {unitInfo?.secondaryUnit === 'OZ' && (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  value={item.quantity_oz}
                                  onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_oz', e.target.value)}
                                  placeholder="0"
                                  className="w-20 h-8 text-center text-sm"
                                  min="0"
                                />
                                <span className="text-xs text-muted-foreground">OZ</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Input 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Restocking for weekend rush"
                data-testid="notes-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={backdateMode ? handleBackdatedSubmit : handleSubmit} 
              disabled={saving}
              className="bg-primary"
              data-testid="submit-transfer-btn"
            >
              {backdateMode ? (
                <>
                  <ClockCounterClockwise size={16} className="mr-2" />
                  {saving ? "Creating..." : "Create Backdated Transfer"}
                </>
              ) : (
                <>
                  <ArrowsLeftRight size={16} className="mr-2" />
                  {saving ? "Creating..." : "Create Transfer"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transfer Dialog (Admin Only) */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setEditDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-wide">EDIT TRANSFER</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Admin: Modify transfer details and date
            </p>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Date Picker for editing */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <Label className="flex items-center gap-2 mb-2">
                <CalendarBlank size={18} />
                Transfer Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarBlank size={18} className="mr-2" />
                    {format(transferDate, 'MMMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={transferDate}
                    onSelect={(date) => {
                      if (date) setTransferDate(date);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Locations */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Location</Label>
                <Select value={fromLocation} onValueChange={setFromLocation}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Location</Label>
                <Select value={toLocation} onValueChange={setToLocation}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Current Items */}
            {transferItems.length > 0 && (
              <div>
                <Label>Items</Label>
                <div className="space-y-2 mt-2">
                  {transferItems.map(item => {
                    const unitInfo = item.item_type === "product" ? parseUnitString(item.item_unit) : null;
                    return (
                      <div key={`${item.item_type}-${item.item_id}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-2">
                          {item.item_type === "prep" ? (
                            <CookingPot size={16} className="text-orange-500" />
                          ) : (
                            <Package size={16} className="text-blue-500" />
                          )}
                          <span className="font-medium text-sm">{item.item_name}</span>
                          <span className="text-xs text-muted-foreground">({item.item_unit})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.item_type === "prep" ? (
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={item.quantity_pans}
                                onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_pans', e.target.value)}
                                className="w-20 h-8 text-center text-sm"
                                min="0"
                              />
                              <span className="text-xs text-muted-foreground">pans</span>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              {unitInfo?.primaryUnit === 'CS' && (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    value={item.quantity_cs}
                                    onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_cs', e.target.value)}
                                    className="w-16 h-8 text-center text-sm"
                                    min="0"
                                  />
                                  <span className="text-xs">CS</span>
                                </div>
                              )}
                              {unitInfo?.secondaryUnit === 'EA' && (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    value={item.quantity_ea}
                                    onChange={(e) => handleItemChange(item.item_id, item.item_type, 'quantity_ea', e.target.value)}
                                    className="w-16 h-8 text-center text-sm"
                                    min="0"
                                  />
                                  <span className="text-xs">EA</span>
                                </div>
                              )}
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRemoveItem(item.item_id, item.item_type)}
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes (Optional)</Label>
              <Input 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Restocking for weekend rush"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              disabled={saving}
              className="bg-primary"
            >
              <Pencil size={16} className="mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
